<template>
	<div class="header-container">
		<Logo />
		<LanguageDropdown :languageList="languageList" :defaultLang="userLang"
			@language-changed="handleLanguageChange" />
	</div>

	<div class="reset-password-page">
		<div class="account-manage inner">
			<div v-if="loading" class="loader-container">
				<div class="simple-spinner"></div>
			</div>

			<div v-else>


				<div>
					<h1 class="password_heading_text">
						{{ translateText(headingMessage) }}
					</h1>

					<p class="introduction">{{ translateText(introMsg) }}</p>

					<form v-if="identifier" @submit.prevent="submitResetPassword">
						<p class="column">
							<label for="password-field">{{ translateText("form_new_password_text") }}</label>
							<input id="password-field" type="text" class="password-field"
								:placeholder="translateText('form_input_new_password_text')" v-model="password" />
						</p>

						<p class="column">
							<label for="cpassword-field">{{ translateText("form_confirm_password_text") }}</label>
							<input id="cpassword-field" type="text" class="password-field"
								:placeholder="translateText('form_input_confirm_password_text')" v-model="cpassword" />
						</p>

						<button type="submit" class="buttonSubmit">
							{{ translateText("submit_label_text") }}
						</button>

						<ul v-if="errorMsgs.length">
							<li v-for="(msgKey, index) in errorMsgs" :key="index">
								{{ translateText(msgKey) }}
							</li>
						</ul>
					</form>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import LanguageDropdown from "@/components/LanguageDropdown.vue";
import Logo from "@/components/logo.vue";
import { getLanguageList, getUserForResetPassword, passwordUpdate } from "@/services/api/auth";
import router from "@/router";
import SuccessMessage from "@/components/SuccessMessage.vue";

const device_language = navigator.language.replace("-", "_");
const password = ref("");
const cpassword = ref("");
const identifier = ref("");
const token = ref("");
const loading = ref(true);
const success = ref(false);
const errorMsgs = ref<string[]>([]);
const languageList = ref<any[]>([]);
const translate = ref<any[]>([]);
const userLang = ref(device_language === "pt_BR" ? "PT" : "EN");
const headingMessage = ref("reset_password_heading_message_text");

const handleLanguageChange = async (lang: string) => {
	userLang.value = lang;
	const storedData: any = localStorage.getItem("translate_text");
	const parsedData = JSON.parse(storedData);
	parsedData.userLang = lang;
	localStorage.setItem("translate_text", JSON.stringify(parsedData));
	localStorage.setItem("userLang", lang);
};

const init = async () => {
	const url = window.location.href;
	const match = url.match(/reset\/(.+)$/);
	const tokenFromUrl = match ? match[1] : null;

	if (!tokenFromUrl) {
		loading.value = false;
		return;
	}

	// Check localStorage success_res
	const storedSuccess = localStorage.getItem("success_res");
	if (storedSuccess) {
		try {
			const parsedSuccess = JSON.parse(storedSuccess);
			if (parsedSuccess?.hash && parsedSuccess.hash === tokenFromUrl) {
				// Token matches stored success hash, redirect immediately
				router.push("/reset/updated");
				return;
			}
		} catch (err) {
			console.error("Failed to parse success_res from localStorage:", err);
		}
	}

	// Token does not match or no stored success, proceed
	token.value = tokenFromUrl;
	await fetchLanguages();
	await fetchUser();
};


const fetchLanguages = async () => {
	try {
		const key = '75 - Reset Password UI';
		const res = await getLanguageList(key);
		languageList.value = res.languageList.length
			? res.languageList.filter((lang: any) => lang.key === "EN" || lang.key === "PT")
			: [];
		translate.value = res.translate || [];

		// Store the full language list + selected language + translation keys
		const headingObj = translate.value.find((item: any) => item.key === "updated_heading_message_text");
		const messageObj = translate.value.find((item: any) => item.key === "updated_message_text");

		const storedData = {
			languageList: languageList.value,
			updated_heading_message_text: headingObj || null,
			updated_message_text: messageObj || null,
		};

		localStorage.setItem("translate_text", JSON.stringify(storedData));
	} catch (err: any) {
		console.error("Failed to fetch language list:", err.message || err);
	}
};


const fetchUser = async () => {
	try {
		const res = await getUserForResetPassword(token.value);
		identifier.value = res?.identifier || "";
		localStorage.setItem("email_for_reset", identifier.value);
		localStorage.setItem("userLang", "EN");
	} catch (err: any) {
		console.error(err.response?.data || err.message);
	} finally {
		loading.value = false;
	}
};

// Translation helper
const translateText = (key: string, params: Record<string, string> = {}): string => {
	if (!translate.value || !Array.isArray(translate.value)) return key;

	const translationItem = translate.value.find((item) => item.key === key);
	if (!translationItem) return key;

	const userLangLower = userLang.value?.toLowerCase();
	const selectedLang = languageList.value.find(
		(lang) => lang.key?.toLowerCase() === userLangLower
	);
	const selectedLangId = selectedLang?._id;

	let translation: any;

	if (selectedLangId) {
		translation = translationItem.translate.find(
			(t: any) => t.languageId === selectedLangId && t.isPublished
		)?.value;
	}

	if (!translation) {
		const enLang = languageList.value.find((lang) => lang.key?.toLowerCase() === "en");
		const enLangId = enLang?._id;
		if (enLangId) {
			translation = translationItem.translate.find(
				(t: any) => t.languageId === enLangId && t.isPublished
			)?.value;
		}
	}

	if (!translation) translation = translationItem.base;

	if (translation && params) {
		Object.keys(params).forEach((k) => {
			const regex = new RegExp(`%this\\.user\\.${k}%`, "g");
			translation = translation.replace(regex, params[k]);
		});
	}

	return translation;
};


const introMsg = computed(() => {
	if (!token.value) {
		return "We cannot find a user matching this account. The token may have expired or be otherwise invalid.";
	}

	if (identifier.value && identifier.value.trim().length > 0) {
		const translationValue = translateText("intro_instruction_message_text", {
			identifier: identifier.value,
		});
		return translationValue || `Please reset the password for ${identifier.value}.`;
	}

	const translationValue = translateText("session_expire_text");
	return translationValue || "The link has expired, Please try again.";
});

const submitResetPassword = async () => {
	errorMsgs.value = [];
	success.value = false;
	const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/;
	const emojiRegex = /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF])/u;

	if (!password.value.trim() || !cpassword.value.trim()) {
		errorMsgs.value.push("enter_password_letter_validation_text");
	}

	if (
		password.value.trim() &&
		cpassword.value.trim() &&
		password.value !== cpassword.value
	) {
		errorMsgs.value.push("password_match_validation_text");
	}

	if (!regex.test(password.value)) {
		errorMsgs.value.push("password_letter_validation_text");
	}

	if (emojiRegex.test(password.value)) {
		errorMsgs.value.push("password_emoji_letter_validation");
	}

	if (!errorMsgs.value.length) {
		const payload = {
			password: password.value,
			token: token.value,
			email: identifier.value,
		};
		const res = await passwordUpdate(payload);
		if (res.valid) {
			success.value = true;

			localStorage.setItem(
				"success_res",
				JSON.stringify({
					hash: token.value,
					api_res: res,
				})
			);

			router.push("/reset/updated");
		}
		else {
			headingMessage.value = "reset_password_heading_message_text";
		}
	}
};

onMounted(() => {
	init();
});
</script>

<style>
.reset-password-page {
	padding: 0.8rem 1em;
	display: flex;
	justify-content: center;
}

.header-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: 1px solid #e0dbdb;
	padding: 0.9rem 1rem, ;
}

body {
	background: white;
	margin: 0 auto;
	box-sizing: border-box;
}

.account-manage {
	max-width: 50rem;
	margin: 0 auto;
	width: 100%;
}

.password_heading_text {
	margin: 20px 0 56px !important;
	letter-spacing: 0.4px;
	line-height: 60px;
	font-style: normal;
	font-weight: 700;
	font-family: 'Fira Sans';
	color: #1D1D1DFF;
}

.introduction {
	margin-bottom: 20px;
}

.column {
	display: flex;
	flex-direction: column;
	margin-bottom: 15px;
}

.column label {
	font-size: 0.8em;
	margin-bottom: 5px;
}

.password-field {
	height: 50px;
	padding: 0.5em;
	border: 1px solid #ccc;
	border-radius: 4px;
}

.password-field:focus {
	border: 1px solid #44142a;
	box-shadow: 0 0 0 1px #44142a;
	outline: none;
}

.buttonSubmit {
	background-color: #44142a;
	border: none;
	color: white;
	padding: 10px 20px;
	margin-top: 25px;
	cursor: pointer;
	border-radius: 4px;
}

.buttonSubmit:focus {
	outline: none;
	box-shadow: 0 0 0 3px #44142a;
}

.loader-container {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 200px;
}

.simple-spinner {
	width: 40px;
	height: 40px;
	border: 4px solid #ccc;
	border-top-color: #44142a;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

ul {
	list-style: disc;
	margin: 10px 0;
	padding-left: 20px;
}
</style>
